/**
 * Ticket Activity Types
 *
 * Types for tracking ticket events, comments, and status changes.
 * Provides user-friendly representations for the activity timeline.
 */

import type {
  TicketStatus,
  WaitingReason,
  EscalationTier,
  AssignmentGroup,
  Priority,
  ApprovalStatus,
  RiskLevel,
} from './ticket';

// ============================================
// ACTIVITY TYPES
// ============================================

export const ActivityType = {
  /** Initial ticket creation */
  CREATED: 'created',
  /** Status changed */
  STATUS_CHANGE: 'status_change',
  /** User or IT staff public comment */
  COMMENT: 'comment',
  /** Internal IT work note (not visible to users) */
  WORK_NOTE: 'work_note',
  /** Assignment change */
  ASSIGNED: 'assigned',
  /** Ticket escalated to different tier/group */
  ESCALATED: 'escalated',
  /** Priority changed */
  PRIORITY_CHANGE: 'priority_change',
  /** Comment or work note was edited */
  EDITED: 'edited',
  /** File attachment added */
  ATTACHMENT: 'attachment',
  /** Ticket resolved */
  RESOLVED: 'resolved',
  /** Ticket reopened */
  REOPENED: 'reopened',
  /** Ticket closed */
  CLOSED: 'closed',
  /** System-generated update */
  SYSTEM: 'system',
  /** IT asking user a question */
  QUESTION_TO_USER: 'question_to_user',
  /** User's answer to a question */
  ANSWER_FROM_USER: 'answer_from_user',

  // === Request-specific activity types ===
  /** Approval status changed (Request/Change tickets) */
  APPROVAL_STATUS_CHANGE: 'approval_status_change',

  // === Problem-specific activity types ===
  /** Root cause analysis updated (Problem tickets) */
  ROOT_CAUSE_UPDATED: 'root_cause_updated',
  /** Workaround added or updated (Problem tickets) */
  WORKAROUND_UPDATED: 'workaround_updated',
  /** Incident linked to problem (Problem tickets) */
  INCIDENT_LINKED: 'incident_linked',
  /** Incident unlinked from problem (Problem tickets) */
  INCIDENT_UNLINKED: 'incident_unlinked',

  // === Change-specific activity types ===
  /** Risk level changed (Change tickets) */
  RISK_LEVEL_CHANGE: 'risk_level_change',
  /** Change ticket approved (Change tickets) */
  CHANGE_APPROVED: 'change_approved',
  /** Change ticket rejected (Change tickets) */
  CHANGE_REJECTED: 'change_rejected',
  /** Implementation window set or updated (Change tickets) */
  IMPLEMENTATION_WINDOW_SET: 'implementation_window_set',
} as const;
export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

// ============================================
// ACTIVITY VISIBILITY
// ============================================

export const ActivityVisibility = {
  /** Visible to everyone */
  PUBLIC: 'public',
  /** Only visible to IT staff */
  INTERNAL: 'internal',
} as const;
export type ActivityVisibility = (typeof ActivityVisibility)[keyof typeof ActivityVisibility];

// ============================================
// ACTIVITY AUTHOR TYPE
// ============================================

export const AuthorType = {
  USER: 'user',
  IT_STAFF: 'it_staff',
  SYSTEM: 'system',
} as const;
export type AuthorType = (typeof AuthorType)[keyof typeof AuthorType];

// ============================================
// ATTACHMENT
// ============================================

export interface TicketAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  uploadedAt: Date;
  uploadedBy: string;
}

// ============================================
// ACTIVITY
// ============================================

/** Edit history entry for tracking changes to comments/work notes */
export interface ActivityEditHistoryEntry {
  /** Original content before edit */
  content: string;
  /** When the edit was made */
  editedAt: Date;
  /** Who made the edit (user ID) */
  editedBy: string;
  /** Display name of editor */
  editedByName: string;
}

export interface TicketActivity {
  id: string;
  ticketId: string;
  type: ActivityType;
  visibility: ActivityVisibility;

  /** Author information */
  authorId: string;
  authorName: string;
  authorType: AuthorType;

  /** Content (for comments and work notes) */
  content?: string;

  /** Status change details */
  previousStatus?: TicketStatus;
  newStatus?: TicketStatus;

  /** Waiting reason when status changes to Waiting */
  waitingReason?: WaitingReason;
  waitingReasonNote?: string;

  /** Priority change details */
  previousPriority?: Priority;
  newPriority?: Priority;

  /** Assignment change details */
  previousAssignedTechId?: string;
  previousAssignedTechName?: string;
  newAssignedTechId?: string;
  newAssignedTechName?: string;

  /** Escalation details */
  previousTier?: EscalationTier;
  newTier?: EscalationTier;
  previousGroup?: AssignmentGroup;
  newGroup?: AssignmentGroup;
  escalationReason?: string;

  /** For edits: reference to original activity being edited */
  originalActivityId?: string;

  /** Edit history - array of previous versions for audit trail */
  editHistory?: ActivityEditHistoryEntry[];

  /** Attachments */
  attachments?: TicketAttachment[];

  /** Timestamp */
  createdAt: Date;

  /** User-friendly message for display */
  displayMessage?: string;

  /** Whether this activity triggers a notification to the user */
  triggersNotification?: boolean;

  /** For QUESTION_TO_USER: The question being asked */
  questionText?: string;

  /** For ANSWER_FROM_USER: Reference to the question activity ID */
  questionActivityId?: string;

  /** For QUESTION_TO_USER: Whether the question has been answered */
  isAnswered?: boolean;

  // === Type-specific activity fields ===

  /** For APPROVAL_STATUS_CHANGE: Previous approval status */
  previousApprovalStatus?: ApprovalStatus;
  /** For APPROVAL_STATUS_CHANGE: New approval status */
  newApprovalStatus?: ApprovalStatus;

  /** For ROOT_CAUSE_UPDATED: The root cause text */
  rootCauseText?: string;

  /** For WORKAROUND_UPDATED: The workaround text */
  workaroundText?: string;

  /** For INCIDENT_LINKED/UNLINKED: The incident ticket ID */
  linkedIncidentId?: string;
  /** For INCIDENT_LINKED/UNLINKED: The incident ticket number */
  linkedIncidentNumber?: string;

  /** For RISK_LEVEL_CHANGE: Previous risk level */
  previousRiskLevel?: RiskLevel;
  /** For RISK_LEVEL_CHANGE: New risk level */
  newRiskLevel?: RiskLevel;

  /** For IMPLEMENTATION_WINDOW_SET: Start time */
  implementationStart?: Date;
  /** For IMPLEMENTATION_WINDOW_SET: End time */
  implementationEnd?: Date;
}

// ============================================
// USER-FRIENDLY STATUS LABELS
// ============================================

/**
 * User-friendly status labels that hide IT internal terminology
 */
export const USER_STATUS_LABELS: Record<TicketStatus, string> = {
  New: 'Submitted',
  'In Progress': 'Being worked on',
  Waiting: 'Waiting for update',
  Resolved: 'Resolved',
  Closed: 'Closed',
};

/**
 * Status descriptions for users
 */
export const USER_STATUS_DESCRIPTIONS: Record<TicketStatus, string> = {
  New: 'Your request has been received and will be reviewed shortly.',
  'In Progress': 'An IT team member is actively working on your request.',
  Waiting: "We're waiting for additional information or resources.",
  Resolved: 'Your request has been completed.',
  Closed: 'This request has been closed.',
};

/**
 * Status colors for visual indication
 */
export const STATUS_COLORS: Record<TicketStatus, { bg: string; text: string; border: string }> = {
  New: { bg: 'rgba(0, 120, 212, 0.1)', text: '#0078d4', border: 'rgba(0, 120, 212, 0.3)' },
  'In Progress': { bg: 'rgba(16, 124, 16, 0.1)', text: '#107c10', border: 'rgba(16, 124, 16, 0.3)' },
  Waiting: { bg: 'rgba(255, 140, 0, 0.1)', text: '#d97706', border: 'rgba(255, 140, 0, 0.3)' },
  Resolved: { bg: 'rgba(16, 124, 16, 0.1)', text: '#107c10', border: 'rgba(16, 124, 16, 0.3)' },
  Closed: { bg: 'rgba(128, 128, 128, 0.1)', text: '#6b7280', border: 'rgba(128, 128, 128, 0.3)' },
};

/**
 * IT-facing status colors (more detailed)
 */
export const IT_STATUS_COLORS: Record<TicketStatus, { bg: string; text: string; border: string }> = {
  New: { bg: '#deecf9', text: '#0078d4', border: '#0078d4' },
  'In Progress': { bg: '#dff6dd', text: '#107c10', border: '#107c10' },
  Waiting: { bg: '#fff4ce', text: '#d97706', border: '#d97706' },
  Resolved: { bg: '#dff6dd', text: '#107c10', border: '#107c10' },
  Closed: { bg: '#e5e7eb', text: '#6b7280', border: '#9ca3af' },
};

// ============================================
// SLA INDICATORS
// ============================================

export const SLAStatus = {
  ON_TRACK: 'on_track',
  AT_RISK: 'at_risk',
  BREACHED: 'breached',
  MET: 'met',
} as const;
export type SLAStatus = (typeof SLAStatus)[keyof typeof SLAStatus];

/**
 * User-friendly SLA messages
 */
export const SLA_MESSAGES: Record<SLAStatus, string> = {
  on_track: 'Response expected soon',
  at_risk: 'Response expected today',
  breached: 'Escalated for priority handling',
  met: 'Response provided',
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get user-friendly label for a status
 */
export function getUserFriendlyStatus(status: TicketStatus): string {
  return USER_STATUS_LABELS[status] ?? status;
}

/**
 * Get status description for user
 */
export function getStatusDescription(status: TicketStatus): string {
  return USER_STATUS_DESCRIPTIONS[status] ?? '';
}

/**
 * Get SLA status based on deadline and current time
 */
export function getSLAStatus(
  slaDeadline: Date | null,
  status: TicketStatus,
  currentTime: Date = new Date()
): SLAStatus {
  // Resolved or Closed tickets have met SLA
  if (status === 'Resolved' || status === 'Closed') {
    return SLAStatus.MET;
  }

  // No deadline set
  if (!slaDeadline) {
    return SLAStatus.ON_TRACK;
  }

  const deadline = new Date(slaDeadline);
  const hoursRemaining = (deadline.getTime() - currentTime.getTime()) / (1000 * 60 * 60);

  if (hoursRemaining < 0) {
    return SLAStatus.BREACHED;
  } else if (hoursRemaining < 4) {
    return SLAStatus.AT_RISK;
  }

  return SLAStatus.ON_TRACK;
}

/**
 * Get user-friendly message for SLA status
 */
export function getSLAMessage(slaStatus: SLAStatus): string {
  return SLA_MESSAGES[slaStatus];
}

/**
 * Format activity for user display, hiding internal details
 */
export function formatActivityForUser(activity: TicketActivity): string {
  switch (activity.type) {
    case ActivityType.CREATED:
      return 'Request submitted';
    case ActivityType.STATUS_CHANGE:
      if (activity.newStatus) {
        return getUserFriendlyStatus(activity.newStatus);
      }
      return 'Status updated';
    case ActivityType.COMMENT:
      return activity.content ?? '';
    case ActivityType.WORK_NOTE:
      // Work notes are internal - should not be shown to users
      return '';
    case ActivityType.ASSIGNED:
      return 'Your request has been assigned to our team';
    case ActivityType.ESCALATED:
      return 'Your request has been escalated for specialized handling';
    case ActivityType.PRIORITY_CHANGE:
      return 'Priority has been updated';
    case ActivityType.EDITED:
      return 'A previous message was updated';
    case ActivityType.ATTACHMENT:
      return 'Attachment added';
    case ActivityType.RESOLVED:
      return 'Request resolved';
    case ActivityType.REOPENED:
      return 'Request reopened';
    case ActivityType.CLOSED:
      return 'Request closed';
    case ActivityType.SYSTEM:
      return activity.displayMessage ?? 'System update';
    case ActivityType.QUESTION_TO_USER:
      return activity.questionText ?? 'A question requires your response';
    case ActivityType.ANSWER_FROM_USER:
      return activity.content ?? '';
    // Type-specific activities - user-friendly messages
    case ActivityType.APPROVAL_STATUS_CHANGE:
      if (activity.newApprovalStatus === 'Approved') {
        return 'Your request has been approved';
      } else if (activity.newApprovalStatus === 'Rejected') {
        return 'Your request could not be approved';
      }
      return 'Approval status updated';
    case ActivityType.ROOT_CAUSE_UPDATED:
    case ActivityType.WORKAROUND_UPDATED:
    case ActivityType.INCIDENT_LINKED:
    case ActivityType.INCIDENT_UNLINKED:
    case ActivityType.RISK_LEVEL_CHANGE:
    case ActivityType.CHANGE_APPROVED:
    case ActivityType.CHANGE_REJECTED:
    case ActivityType.IMPLEMENTATION_WINDOW_SET:
      // These are IT-internal activities, not shown to users
      return '';
    default:
      return '';
  }
}

/**
 * Format activity for IT staff display (includes internal details)
 */
export function formatActivityForIT(activity: TicketActivity): string {
  switch (activity.type) {
    case ActivityType.CREATED:
      return `Ticket created by ${activity.authorName}`;
    case ActivityType.STATUS_CHANGE:
      if (activity.newStatus === 'Waiting' && activity.waitingReason) {
        return `Status changed to Waiting - ${activity.waitingReason}`;
      }
      return `Status changed to ${activity.newStatus}`;
    case ActivityType.COMMENT:
      return activity.content ?? '';
    case ActivityType.WORK_NOTE:
      return activity.content ?? '';
    case ActivityType.ASSIGNED:
      if (activity.newAssignedTechName) {
        return `Assigned to ${activity.newAssignedTechName}`;
      }
      return 'Ticket assigned';
    case ActivityType.ESCALATED:
      const tierPart = activity.newTier ? `to ${activity.newTier}` : '';
      const groupPart = activity.newGroup ? ` (${activity.newGroup})` : '';
      return `Escalated ${tierPart}${groupPart}`;
    case ActivityType.PRIORITY_CHANGE:
      return `Priority changed from ${activity.previousPriority} to ${activity.newPriority}`;
    case ActivityType.EDITED:
      return 'Entry edited';
    case ActivityType.ATTACHMENT:
      return 'Attachment added';
    case ActivityType.RESOLVED:
      return 'Ticket resolved';
    case ActivityType.REOPENED:
      return 'Ticket reopened';
    case ActivityType.CLOSED:
      return 'Ticket closed';
    case ActivityType.SYSTEM:
      return activity.displayMessage ?? 'System update';
    case ActivityType.QUESTION_TO_USER:
      return `Question asked: ${activity.questionText ?? ''}`;
    case ActivityType.ANSWER_FROM_USER:
      return `User answered: ${activity.content ?? ''}`;
    // Type-specific activities
    case ActivityType.APPROVAL_STATUS_CHANGE:
      return `Approval status changed from ${activity.previousApprovalStatus ?? 'N/A'} to ${activity.newApprovalStatus}`;
    case ActivityType.ROOT_CAUSE_UPDATED:
      return 'Root cause analysis updated';
    case ActivityType.WORKAROUND_UPDATED:
      return 'Workaround updated';
    case ActivityType.INCIDENT_LINKED:
      return `Incident ${activity.linkedIncidentNumber ?? activity.linkedIncidentId} linked to problem`;
    case ActivityType.INCIDENT_UNLINKED:
      return `Incident ${activity.linkedIncidentNumber ?? activity.linkedIncidentId} unlinked from problem`;
    case ActivityType.RISK_LEVEL_CHANGE:
      return `Risk level changed from ${activity.previousRiskLevel ?? 'N/A'} to ${activity.newRiskLevel}`;
    case ActivityType.CHANGE_APPROVED:
      return 'Change request approved';
    case ActivityType.CHANGE_REJECTED:
      return 'Change request rejected';
    case ActivityType.IMPLEMENTATION_WINDOW_SET:
      return 'Implementation window updated';
    default:
      return '';
  }
}

/**
 * Check if an activity requires user action
 */
export function activityRequiresUserAction(activity: TicketActivity): boolean {
  // Waiting with reason 'User' requires user action
  if (
    activity.type === ActivityType.STATUS_CHANGE &&
    activity.newStatus === 'Waiting' &&
    activity.waitingReason === 'User'
  ) {
    return true;
  }

  // Unanswered question requires user action
  if (activity.type === ActivityType.QUESTION_TO_USER && !activity.isAnswered) {
    return true;
  }

  return false;
}

/**
 * Generate activity entries from ticket data (for initial/mock data)
 */
export function generateInitialActivities(
  ticketId: string,
  requesterName: string,
  createdAt: Date
): TicketActivity[] {
  return [
    {
      id: `${ticketId}-created`,
      ticketId,
      type: ActivityType.CREATED,
      visibility: ActivityVisibility.PUBLIC,
      authorId: 'system',
      authorName: requesterName,
      authorType: AuthorType.USER,
      displayMessage: 'Request submitted',
      createdAt,
    },
  ];
}
